import { useGetAuthMe } from "@birdwatch/client";
import { useUser } from "@clerk/clerk-react";
import { type PropsWithChildren, useEffect } from "react";
import { useImpersonationSession } from "@/features/shared/impersonation";
import { useAnalytics } from "@/libs/analytics";

export const AnalyticIdentify = ({ children }: PropsWithChildren) => {
  const { isLoaded, isSignedIn } = useUser();
  const impersonation = useImpersonationSession();
  const { identifyUser, linkAnonymousToUser } = useAnalytics();
  /**
   * Gated so this can also mount on the PUBLIC presale funnel, whose first two steps are
   * signed out: `/auth/me` with no token is a 401 the client retries three times with
   * backoff (`routes/__root.tsx`). Under `(protected)` this changes nothing —
   * `ProtectedProvider` guarantees a session there, so `enabled` is always true.
   *
   * `isLoading` (not `isPending`) is what makes the disabled case safe: a disabled query is
   * pending forever, but `isLoading` is false, so the effect just short-circuits on `!me`.
   */
  const { data: me, isLoading } = useGetAuthMe().get({
    enabled: isLoaded && Boolean(isSignedIn),
  });

  useEffect(() => {
    // An admin browsing as a customer must not be identified as that customer
    // in analytics; the frame stays anonymous.
    if (impersonation) return;
    // Don't identify while loading to avoid using empty values
    if (isLoading || !me?.id) return;
    const accountId = me?.accounts?.[0]?.id;
    linkAnonymousToUser(me?.id);
    identifyUser(me?.id, {
      $email: me?.email,
      $name: me?.name,
      $phone: me?.phone || undefined,
      $account_id: accountId,
    });
  }, [me?.id, isLoading, impersonation]);

  return children;
};
